import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import { APOffer } from "@/apps/tatar/activityApp/views/details/offer-feature/APOffer.Interface";
import ErrorAnimation from "@/components/LottieAnimations/ErrorAnimation/ErrorAnimation";
import SuccessAnimation from "@/components/LottieAnimations/Success/SuccessAnimation";
import Log from "@/debug/Log";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import i18n from "@/i18n";
import { HTTP } from "@/utils/Http";
import { useState, useEffect, useRef } from "react";
import {
  APOfferVacancyMeta,
  APOfferVacancyContractAttachmentBoldSign,
} from "../../../model/VacancyApplicant.interface";
import "./VacancyContractDigitalSigning.scss";
import { Loader } from "rsuite";
import { useTypedSelector } from "@/redux/hooks";

type Props = {
  activity: APActivity;
  offer: APOffer;
  closeModal: () => void;
  reload: () => void;
};

const VacancyContractDigitalSigning = (props: Props) => {
  const { offer, closeModal, reload } = props;

  const user = useTypedSelector((state) => state.global.user);

  const [signLink, setSignLink] = useState<string | undefined>();
  const [contractSigned, setContractSigned] = useState(false);
  const [contractSignError, setContractSignError] = useState(false);

  const offerMeta = offer.data.meta as APOfferVacancyMeta;
  const [contract] = offerMeta.contracts;

  useEffect(() => {
    if (!contractSigned) {
      return;
    }

    const timeoutId = setTimeout(() => {
      reload();
      closeModal();
    }, 1400);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [closeModal, contractSigned, reload]);

  const fetchEmbeddedSignUrlFn = async () => {
    try {
      const contractBold = contract as APOfferVacancyContractAttachmentBoldSign;
      const response = await HTTP.post({
        url: `/vacancy/contract/${contractBold.documentId}/getSignLink`,
        target: "EMPTY",
        bodyParams: {
          email: user.email,
        },
      });
      if (response.error) {
        Log.error(response.error);
        setContractSignError(true);
        return;
      }
      setSignLink(response.signLink);
    } catch (error) {
      Log.error(error);
    }
  };

  const { execute, loading } = useAsyncAction({
    asyncFn: fetchEmbeddedSignUrlFn,
  });

  useEffect(() => {
    execute();
  }, [offer]);

  const showIframe =
    signLink !== undefined &&
    signLink !== null &&
    !contractSigned &&
    !loading &&
    !contractSignError;

  return (
    <div className="vacancy-contract-sign-content">
      {loading && (
        <div className="loading-mask">
          <div>
            <Loader size="lg" />
          </div>
          <div className="__sub-text">
            {i18n.t(
              "apTemplate:Vacancy.Applicant.ContractDetails.Label.Loading",
              "Lade Mietvertrag..."
            )}
          </div>
        </div>
      )}
      {showIframe && (
        <BoldSignIframe
          src={signLink}
          onSigned={() => {
            setContractSigned(true);
          }}
          onSigningFailed={() => {
            setContractSignError(true);
          }}
        />
      )}
      {contractSigned && (
        <div className="__height-100 __flex __flex-column __align-center __justify-center __width-100">
          <div className="__h3">
            {i18n.t(
              "apTemplate:Vacancy.Applicant.ContractDetails.Label.ContractSigned",
              "Der Mietvertrag wurde erfolgreich unterschrieben."
            )}
          </div>
          <SuccessAnimation style={{ width: 250, height: 250 }} />
        </div>
      )}
      {contractSignError && (
        <div className="loading-mask">
          <ErrorAnimation style={{ width: 250, height: 250 }} />
          <div className="__sub-text">
            {i18n.t(
              "apTemplate:Vacancy.Applicant.ContractDetails.Label.ContractSignError",
              "Beim Laden des Mietvertrags ist ein Fehler aufgetreten."
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VacancyContractDigitalSigning;

interface BoldSignIframeProps {
  src: string;
  onSigned?: () => void;
  onSigningFailed?: () => void;
  onReassigned?: () => void;
  onReassigningFailed?: () => void;
  onDeclined?: () => void;
  onDecliningFailed?: () => void;
  style?: React.CSSProperties;
}

const BOLDSIGN_ORIGIN = "https://app-eu.boldsign.com";
const BoldSignIframe: React.FC<BoldSignIframeProps> = ({
  src,
  onSigned,
  onSigningFailed,
  onReassigned,
  onReassigningFailed,
  onDeclined,
  onDecliningFailed,
  style,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // 1) make sure it really comes from BoldSign:
      if (event.origin !== BOLDSIGN_ORIGIN) return;

      const action = event.data?.action as string;
      switch (action) {
        case "onDocumentSigned":
          onSigned?.();
          break;
        case "onDocumentSigningFailed":
          onSigningFailed?.();
          break;
        case "onDocumentReassigned":
          onReassigned?.();
          break;
        case "onDocumentReassigningFailed":
          onReassigningFailed?.();
          break;
        case "onDocumentDeclined":
          onDeclined?.();
          break;
        case "onDocumentDecliningFailed":
          onDecliningFailed?.();
          break;
        default:
          // you might want to console.debug here
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    onSigned,
    onSigningFailed,
    onReassigned,
    onReassigningFailed,
    onDeclined,
    onDecliningFailed,
  ]);

  return (
    <iframe
      ref={iframeRef}
      src={src}
      title={i18n.t(
        "apTemplate:Vacancy.Applicant.ContractDetails.Label.IFrameTitle",
        "Mietvertrag unterschreiben"
      )}
      style={{
        border: 0,
        width: "100%",
        height: "600px",
        ...style,
      }}
      // allow="camera; microphone" // if BoldSign uses these
    />
  );
};
